import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import Logo from "../../assets/fillopsLogo.png";
import CartIcon from "../CartIcon";
import UserNavBar2 from "../UserNavBar2";
import authSession from "../../lib/authSession";
import {useUserAuth} from "../../store/user";

/**
 * White nav bar shown on the course details page.
 * Logo · search · cart · login · sign up.
 */
export default function CourseNavbar() {
  const navigate = useNavigate();
  const {userIsAuthenticated} = useUserAuth();
  const authSnapshot = React.useSyncExternalStore(authSession.subscribe, authSession.getSnapshot);
  const authenticated = userIsAuthenticated || (authSnapshot.status === "authenticated" && authSnapshot.role === "user");
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const submitSearch = (event) => {
    event.preventDefault();
    const q = query.trim();
    navigate(q ? `/courses?search=${encodeURIComponent(q)}` : "/courses");
  };

  if (authenticated) return <UserNavBar2 />;

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
        {/* Logo — left */}
        <Link to="/" className="flex shrink-0 no-underline items-center gap-2">
          <img src={Logo} alt="Fillop" className="h-8 w-auto" />
          <span className="hidden text-xl no-underline font-bold text-[#0e3b9a] sm:block">Fillop</span>
        </Link>

        {/* Search — middle */}
        <div className="hidden flex-1 md:flex md:justify-center">
          <form onSubmit={submitSearch} className="relative w-full max-w-xl">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search for courses…"
              aria-label="Search for courses"
              className="w-full rounded-full border border-slate-300 bg-slate-50 py-2 pl-9 pr-4 text-sm text-slate-800 outline-none transition-colors focus:border-blue-900 focus:bg-white"
            />
          </form>
        </div>

        <div className="flex-1 md:hidden" />

        {/* Cart — shared dropdown icon */}
        <CartIcon />

        {/* Auth — desktop */}
        <div className="hidden shrink-0 items-center gap-1 sm:flex">
          <Link
            to="/login"
            className="rounded-md no-underline px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:text-blue-900"
          >
            Login
          </Link>
          <Link
            to="/register"
            className="rounded-md no-underline bg-blue-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
          >
            Sign up
          </Link>
        </div>

        {/* Mobile menu toggle */}
        <button
          type="button"
          aria-label="Menu"
          onClick={() => setMenuOpen((open) => !open)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 sm:hidden"
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="border-t border-slate-100 bg-white px-4 py-3 sm:hidden">
          <form onSubmit={submitSearch} className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search for courses…"
              aria-label="Search for courses"
              className="w-full rounded-full border border-slate-300 bg-slate-50 py-2 pl-9 pr-4 text-sm text-slate-800 outline-none focus:border-blue-900 focus:bg-white"
            />
          </form>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              className="flex-1 no-underline rounded-md border border-slate-200 px-4 py-2 text-center text-sm font-medium text-slate-700"
            >
              Login
            </Link>
            <Link
              to="/register"
              onClick={() => setMenuOpen(false)}
              className="flex-1 rounded-md no-underline bg-blue-900 px-4 py-2 text-center text-sm font-semibold text-white"
            >
              Sign up
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
